import React from 'react'
import { FaStar, FaCheck } from 'react-icons/fa'
import classnames from 'classnames'

import {
	type CollaborativeCulminatingMomentScreen,
	type JuniorTeam,
	type StudentSpecificContextualDataEntry,
	getDocumentBeingGraded,
	getNextDocumentToGrade,
} from '@mission.io/mission-toolkit'
import {
	COLLABORATIVE_CULMINATING_MOMENT,
	CREATIVE_CANVAS_STATION,
} from '@mission.io/mission-toolkit/constants'
import {
	useCreativeCanvasData,
	useJuniorPlusStateSelector,
	useMissionStateSelector,
} from '../../../../context/customHooks'
import type {
	CreativeCanvasStationIssue,
	CreativeCanvasStationCriterion,
} from '@mission.io/mission-toolkit'
import { styled } from 'styled-components'
import {
	useCanControlMission,
	useSendWebsocketMessage,
} from '../../../../context/provider'
import { CreativeCanvasDocument } from './CreativeCanvas'
import {
	CulminatingMomentHeader,
	MissionFrameFooterSpacer,
} from '../sharedComponents'
import { MultiMediaCard } from './MultiMediaCard/MultiMediaCard'
import { useTeamDataForCreativeCanvas } from '../../../../utility/selectors'
import { BackgroundVideo } from '../../../frames/BasicFrame'
import { Checkbox } from '../../../basics/Checkbox'
import { BACKGROUND_VIDEO_URL_NO_LOGO } from '../../../../assets/constants'
import { Button } from '../../../basics/Button'

/**
 * A component which displays the grading step of the collaborative culminating moment screen. Allows grading the documents one by one.
 */
export function CollaborativeCulminatingMomentGrading({
	screen,
	gradingStep,
}: {
	screen: CollaborativeCulminatingMomentScreen,
	gradingStep: {|
		status:
			| typeof COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.GRADING_ALL
			| typeof COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.GRADING_HIDDEN
			| typeof COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.GRADING_UNHIDDEN,
		qualifiedDocuments: Array<{ documentId: string }>,
		currentlyGrading: ?string,
	|},
}): React$Node {
	const creativeCanvas = useCreativeCanvasData(screen.canvasId)
	const sendWebsocketMessage = useSendWebsocketMessage()

	const currentDocumentId = useMissionStateSelector(getDocumentBeingGraded)
	const nextDocumentIdToGrade = useMissionStateSelector(getNextDocumentToGrade)

	const currentDocumentInfo = creativeCanvas?.teams.find(
		({ document }) => document.id === currentDocumentId
	)
	const nextDocumentInfo = creativeCanvas?.teams.find(
		({ document }) => document.id === nextDocumentIdToGrade
	)

	const team = useTeam(currentDocumentInfo?.teamId)
	const nextTeam = useTeam(nextDocumentInfo?.teamId)

	return (
		<FlexWrapper>
			<BackgroundVideo src={BACKGROUND_VIDEO_URL_NO_LOGO} />
			<CulminatingMomentHeader
				rightChild={
					nextTeam && <PadRight>Next Team: {nextTeam.displayName}</PadRight>
				}>
				{team?.displayName || 'Creative Canvas'}
			</CulminatingMomentHeader>

			{currentDocumentId && currentDocumentInfo ? (
				<CreativeCanvasDocumentWrapper
					// Use `currentDocumentId` as key so that the component is re-mounted when the document changes
					key={currentDocumentId}
					issue={creativeCanvas?.canvasData.issues[currentDocumentInfo.issueId]}
					teamId={currentDocumentInfo.teamId}
					canvasActionId={screen.canvasId}
					grades={screen.scores.documents[currentDocumentId]}
					onSelectOption={({ criteriaId, optionId }) => {
						sendWebsocketMessage({
							type: 'COLLABORATIVE_CULMINATING_MOMENT_GRADE_DOCUMENT_CRITERION',
							payload: {
								screenId: screen._actionId,
								documentId: currentDocumentId,
								criteriaId,
								optionId,
							},
						})
					}}
					onFinishDocument={() => {
						sendWebsocketMessage({
							type: 'COLLABORATIVE_CULMINATING_MOMENT_FINISH_GRADING_CURRENT_DOCUMENT',
							payload: {
								documentId: currentDocumentId,
								screenId: screen._actionId,
							},
						})
					}}
					isLastDocument={!nextDocumentIdToGrade}
					showHiddenCriteria={[
						COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.GRADING_ALL,
						COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.GRADING_HIDDEN,
					].includes(gradingStep.status)}>
					<CreativeCanvasDocument
						canvasId={screen.canvasId}
						documentId={currentDocumentId}
						key={currentDocumentId}
					/>
				</CreativeCanvasDocumentWrapper>
			) : (
				<Center>No document to grade</Center>
			)}
			<MissionFrameFooterSpacer />
		</FlexWrapper>
	)
}

/**
 * Displays a single creative canvas document, along with the related issue and contextual data.
 *
 * @param props.teamId The id of the team for the document.
 */
function CreativeCanvasDocumentWrapper({
	issue,
	canvasActionId,
	teamId,
	children,
	grades,
	onSelectOption,
	onFinishDocument,
	isLastDocument,
	showHiddenCriteria,
}: {
	issue: ?CreativeCanvasStationIssue,
	canvasActionId: string,
	teamId: string,
	children?: React$Node,
	grades: ?{ criteria: { [criterionId: string]: { selectedOption: string } } },
	onSelectOption: (option: { criteriaId: string, optionId: string }) => mixed,
	onFinishDocument: () => mixed,
	isLastDocument: boolean,
	showHiddenCriteria: boolean,
}) {
	const isFullyGraded = issue?.rubric.criteria.every(criterion => {
		const isYesNo = criterion.gradingOptions.every(
			option => option.text === 'Yes' || option.text === 'No'
		)
		if (
			// We don't need teachers to grade hidden criteria
			(criterion.displayType ===
				CREATIVE_CANVAS_STATION.RUBRIC.CRITERIA.DISPLAY_TYPE.HIDDEN &&
				!showHiddenCriteria) ||
			// Teachers can leave yes/no criteria ungraded
			isYesNo
		) {
			return true
		}

		return grades?.criteria[criterion.id]?.selectedOption
	})
	const canvasData = useTeamDataForCreativeCanvas(canvasActionId, teamId)

	const putRubricInTabs = issue ? issue.rubric.criteria.length >= 4 : false

	return (
		<GridWrapper $showData={canvasData.length > 0 ? !putRubricInTabs : false}>
			<PromptContainer>
				<h3 className="font-game-body text-[calc(var(--ph)*3)]">
					{issue?.prompt}
				</h3>
			</PromptContainer>
			<DocumentContainer>{children}</DocumentContainer>
			{issue && (
				<>
					<Rubric
						style={{ gridArea: 'rubric' }}
						includeDataTab={
							putRubricInTabs ? { canvasData, teamId } : undefined
						}
						criteria={issue.rubric.criteria}
						showHiddenCriteria={showHiddenCriteria}
						onGradeCriteria={onSelectOption}
						grades={grades}>
						<Button
							className="float-right mt-2"
							disabled={!isFullyGraded}
							onClick={() => {
								// If the criteria is ungraded and there is no "No" option, then we cannot proceed
								let ableToSubmit = true
								// Submit the "No" option for each of the ungraded criteria
								issue.rubric.criteria.forEach(criterion => {
									if (
										criterion.displayType ===
											CREATIVE_CANVAS_STATION.RUBRIC.CRITERIA.DISPLAY_TYPE
												.HIDDEN &&
										!showHiddenCriteria
									) {
										return
									}

									if (!grades?.criteria[criterion.id]?.selectedOption) {
										const selectedOption = criterion.gradingOptions.find(
											option => option.text === 'No'
										)
										if (!selectedOption) {
											ableToSubmit = false
											return
										}
										// I think this should be moved to the back end
										onSelectOption({
											criteriaId: criterion.id,
											optionId: selectedOption.id,
										})
									}
								})
								if (ableToSubmit) {
									onFinishDocument()
								}
							}}>
							{isLastDocument ? 'Submit' : 'Next'}
						</Button>
					</Rubric>
					{/* Data will be displayed in the Rubric component if stackRubricAndData is true */}
					{!putRubricInTabs && canvasData.length > 0 && (
						<Tabs
							style={{ gridArea: 'data' }}
							content={[
								{
									headerIcon: FaStar,
									headerTitle: 'Data',
									theme: 'dark',
									tabContent: <CanvasData data={canvasData} />,
								},
							]}
						/>
					)}
				</>
			)}
		</GridWrapper>
	)
}

/**
 * Returns the team with the given `teamId`.
 */
export function useTeam(teamId: ?string): ?JuniorTeam {
	return useJuniorPlusStateSelector(state => {
		if (!teamId) {
			return null
		}
		return state?.teams[teamId]
	})
}

const FlexWrapper = styled.div`
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: var(--spacing-2x);
`

const GridWrapper = styled.div`
	height: 100%;
	display: grid;
	grid-template:
		'prompt rubric' auto
		'document rubric' auto
		'document ${({ $showData }) =>
			$showData === true ? 'data' : 'rubric'}' 1fr
		/ 50% 1fr;

	gap: var(--spacing-2x) var(--spacing-4x);
	padding: 0 var(--spacing-4x);
	min-height: 0;
	.ccm-review-scrollbar {
		&::-webkit-scrollbar {
			width: 8px;
			height: 8px;
		}

		&::-webkit-scrollbar-thumb {
			background: rgba(255, 255, 255, 0.55);
			border-radius: 100vh;
		}

		&::-webkit-scrollbar-thumb:hover {
			background: rgba(255, 255, 255, 0.33);
		}
	}
`

/**
 * Displays the rubric for a creative canvas document.
 */
function Rubric({
	includeDataTab,
	criteria,
	showHiddenCriteria,
	onGradeCriteria,
	grades,
	className,
	style,
	children,
}: {
	className?: string,
	includeDataTab: {
		canvasData: Array<StudentSpecificContextualDataEntry>,
		teamId: string,
	} | void,
	criteria: Array<CreativeCanvasStationCriterion>,
	showHiddenCriteria: boolean,
	onGradeCriteria: (option: { criteriaId: string, optionId: string }) => mixed,
	grades: ?{ criteria: { [criterionId: string]: { selectedOption: string } } },
	style?: { [string]: string | number },
	children?: React$Node,
}): React$Node {
	const canControlMission = useCanControlMission()

	return (
		<Tabs
			style={style}
			className={[className, 'h-full'].filter(Boolean).join(' ')}
			content={[
				{
					headerIcon: FaCheck,
					headerTitle: 'Rubric',
					theme: 'base',
					tabContent: (
						<div className="p-2 bg-white/20 rounded-sm h-full overflow-auto ccm-review-scrollbar">
							<ol className="space-y-2 pl-0 mt-0 list-none">
								{criteria.map((criterion, i) => {
									const isHidden =
										criterion.displayType ===
											CREATIVE_CANVAS_STATION.RUBRIC.CRITERIA.DISPLAY_TYPE
												.HIDDEN && !showHiddenCriteria
									const useCheckbox = criterion.gradingOptions.every(
										option => option.text === 'Yes' || option.text === 'No'
									)
									const selectedOption = criterion.gradingOptions.find(
										option =>
											option.id ===
											grades?.criteria[criterion.id]?.selectedOption
									)

									const isCorrect = selectedOption?.correct

									const disabled = !canControlMission || isHidden

									return (
										<li
											key={criterion.id}
											className={isHidden ? 'blur-sm' : ''}>
											<label
												className={classnames(
													!disabled && 'cursor-pointer hover:bg-white/10',
													isCorrect
														? 'border-2 border-game-accent-green'
														: 'border-2 border-transparent',
													`flex list-none list-outside rounded overflow-hidden text-[calc(var(--ph)*2)]`
												)}>
												<span
													className={classnames(
														'flex items-center tabular-num px-2 bg-game-primary-green rounded-l'
													)}>
													{i + 1}
												</span>
												<span
													className={classnames(
														'flex-1 flex items-center justify-between px-2 py-0.5 bg-white/60 text-black min-h-[3em] rounded-r'
													)}>
													{isHidden
														? replaceAlphabetCharactersWithQuestionMarks(
																criterion.text
														  )
														: criterion.text}
													{useCheckbox ? (
														<Checkbox
															{...{
																checked: selectedOption?.text === 'Yes',
																onChange: checked => {
																	if (!canControlMission) {
																		return
																	}
																	const selectedOption =
																		criterion.gradingOptions.find(option => {
																			return (
																				option.text === (checked ? 'Yes' : 'No')
																			)
																		})
																	if (!selectedOption) {
																		return
																	}
																	onGradeCriteria({
																		criteriaId: criterion.id,
																		optionId: selectedOption.id,
																	})
																},
																disabled,
															}}
														/>
													) : (
														<select
															{...{
																value:
																	grades?.criteria[criterion.id]
																		?.selectedOption,
																onChange: e => {
																	onGradeCriteria({
																		criteriaId: criterion.id,
																		optionId: e.target.value,
																	})
																},
																disabled,
															}}>
															<option />
															{criterion.gradingOptions.map(option => {
																return (
																	<option key={option.id} value={option.id}>
																		{option.text}
																	</option>
																)
															})}
														</select>
													)}
												</span>
											</label>
										</li>
									)
								})}
							</ol>
							{children}
						</div>
					),
				},
				includeDataTab
					? {
							headerIcon: FaStar,
							headerTitle: 'Data',
							theme: 'dark',
							tabContent: <CanvasData data={includeDataTab.canvasData} />,
					  }
					: null,
			].filter(Boolean)}
		/>
	)
}

/**
 * Displays a list of contextual data for the canvas.
 *
 * @param props.teamId The id of the team for the document.
 */
function CanvasData({
	className,
	data,
}: {
	data: Array<StudentSpecificContextualDataEntry>,
	className?: string,
}): React$Node {
	return (
		<div
			className={[
				className,
				'space-y-2 px-2 overflow-y-auto ccm-review-scrollbar h-full overflow-x-hidden',
			]
				.filter(Boolean)
				.join(' ')}>
			{data.map((item, index) => {
				return <MultiMediaCard key={index} content={item} />
			})}
		</div>
	)
}

const PromptContainer = styled.div`
	grid-area: prompt;
	background-color: ${({ theme }) => theme.primaryColor};
	padding: var(--spacing-2x);
	border-radius: 16px;
	h3 {
		margin: 0;
	}
`

const DocumentContainer = styled.div`
	justify-self: center;
	width: 100%;
	grid-area: document;
	aspect-ratio: 4 /3;
	height: 100%;
	width: fit-content;
`

const PadRight = styled.span`
	padding-right: var(--spacing-2x);
`

const Center = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
`

/**
 * replaceAlphabetCharactersWithQuestionMarks - replaces all alphabet characters in a string with question marks
 *
 * @param {string} str - the string to replace characters in
 *
 * @return {string} - str with all alphabet characters replaced with question marks
 */
function replaceAlphabetCharactersWithQuestionMarks(str: string): string {
	// $FlowExpectedError[prop-missing] replaceAll is a relatively new function call
	return str.replaceAll(/[a-z]/gi, '?')
}

/**
 * A way to display tabs. Each tab is defined in the `content' array.
 * A tab has a header with an icon and a title. The content of the tab is displayed when the tab is clicked. By default the first item in the content array is selected.
 * @param {Object} props
 * @param {{[string]: string|number}} props.style - The style to apply to the tab container
 * @param {string} props.className - The class name to apply to the tab container
 * @param {Array<>} props.content - An array of objects defining the content of each tab. See below
 * @param {React$ComponentType<{ className?: string }>} props.content.headerIcon - the icon to display next to the title
 * @param {string} props.content.headerTitle - The title of the tab
 * @param {React$Node} props.content.tabContent - The content to display when the tab is clicked
 * @param {'dark'|'base'} props.content.theme - The theme of the tab, for display only.
 * @returns {React$Node}
 */
function Tabs({
	style,
	className,
	content,
}: {
	className?: string,
	style?: { [string]: string | number },
	content: Array<{
		headerIcon: React$ComponentType<{ className?: string }>,
		headerTitle: string,
		tabContent: React$Node,
		theme: 'dark' | 'base',
	}>,
}): React$Node {
	const [selectedTab, setSelectedTab] = React.useState(0)
	return (
		<div
			className={[className, 'flex flex-col min-h-0'].filter(Boolean).join(' ')}
			style={style}>
			<div className="flex gap-1">
				{content.map(
					({ headerIcon: HeaderIcon, headerTitle, theme }, index) => (
						<button
							className={`font-game-headline flex gap-1 p-2 rounded-t-lg ${
								theme === 'dark' ? 'bg-game-primary-700' : 'bg-game-primary-600'
							} ${content.length === 1 ? 'w-1/2' : 'w-fit pr-10'}`}
							key={headerTitle}
							onClick={() => setSelectedTab(index)}>
							<HeaderIcon className="text-game-accent-green my-auto" />
							<H4Header>{headerTitle}</H4Header>
						</button>
					)
				)}
			</div>
			<div
				className={`text-white w-full p-2 flex-1 overflow-hidden rounded-b-lg rounded-tr-lg min-h-[44px] ${
					content[selectedTab].theme === 'dark'
						? 'bg-game-primary-700'
						: 'bg-game-primary-600'
				}`}>
				{content[selectedTab].tabContent}
			</div>
		</div>
	)
}

const H4Header = styled.h4.attrs({ className: 'font-game-headline' })`
	margin: 0;
	font-weight: 400;
	font-size: calc(var(--ph) * 2);
`
